import qs from 'qs';
import {Pageable} from '@deviceinsight/ng-ui-api-client';
import {getAxios} from '@deviceinsight/ng-ui-api-client';
import {Rule, UiRule, UiCondition} from '../modules/ruleengine/types';
import {
	mapApiRuleToUiRule,
	mapUiRuleToApiRule,
	mapApiConditionToUiCondition
} from '../components/ruleengine/ruleEditHelpers';
import {SearchSettings} from '../components/ruleengine/ruleListeHelpers';
import {displayErrorMessage} from './apiUtils';

const getBaseApi = (assetGroupId: number) => `/rafi/v1/assetGroups/${assetGroupId}/rules`;

const searchRules = (searchSettings: SearchSettings, assetGroupId: number): Promise<Pageable<Rule>> => {
	const {paginationSettings, nameFilter} = searchSettings;
	const {
		page,
		size,
		sort: {property, direction}
	} = paginationSettings;
	const params = {
		name: nameFilter,
		page: page || 0,
		size,
		sort: `${property},${direction}`,
		ignoreCase: true
	};
	return getAxios()
		.get(`${getBaseApi(assetGroupId)}?${qs.stringify(params)}`)
		.then((response) => response.data)
		.catch((e) => {
			displayErrorMessage(e, 'RuleEngine');
		});
};

const loadRule = (ruleId: string, assetGroupId: number): Promise<UiRule | void> => {
	return getAxios()
		.get(`${getBaseApi(assetGroupId)}/${ruleId}`)
		.then(({data: rule}) => mapApiRuleToUiRule(rule))
		.catch((e) => {
			displayErrorMessage(e, 'RuleEngine');
		});
};

const insertUiRule = (uiRule: UiRule): Promise<UiRule | void> => {
	// First Map UI Rule to Rule
	const rule: Rule = mapUiRuleToApiRule(uiRule);
	return getAxios()
		.post(getBaseApi(uiRule.assetGroupId), rule)
		.then(({data: rule}) => mapApiRuleToUiRule(rule))
		.catch((e) => {
			displayErrorMessage(e, 'RuleEngine');
		});
};

const updateUiRule = (uiRule: UiRule, savedUiRule?: UiRule): Promise<UiRule> => {
	// First Map UI Rule to Rule
	const rule: Rule = mapUiRuleToApiRule(uiRule, savedUiRule);
	return updateRule(rule).then((rule) => mapApiRuleToUiRule(rule));
};

const updateRule = (rule: Rule): Promise<Rule> => {
	const {id, assetGroupId} = rule;
	return getAxios()
		.put(`${getBaseApi(assetGroupId)}/${id}`, rule)
		.then(({data: rule}) => rule)
		.catch((e) => {
			displayErrorMessage(e, 'RuleEngine');
		});
};

const saveUiRule = (uiRule: UiRule, savedUiRule?: UiRule): Promise<UiRule | void> => {
	if (uiRule.id) {
		return updateUiRule(uiRule, savedUiRule);
	} else {
		return insertUiRule(uiRule);
	}
};

const deleteRule = (ruleID: string, assetGroupId: number): Promise<any> => {
	return getAxios()
		.delete(`${getBaseApi(assetGroupId)}/${ruleID}`)
		.catch((e) => {
			displayErrorMessage(e, 'RuleEngine');
		});
};

const getTriggerDetails = (ruleID: string, assetGroupId: number): Promise<UiCondition | void> => {
	return getAxios()
		.get(`${getBaseApi(assetGroupId)}/${ruleID}/triggerConditions`)
		.then((response) => mapApiConditionToUiCondition(response.data))
		.catch((e) => {
			displayErrorMessage(e, 'RuleEngine');
		});
};

const resetCounter = (conditionId: string, ruleId: string, assetGroupId: number) => {
	return getAxios()
		.put(`${getBaseApi(assetGroupId)}/${ruleId}/triggerConditions/${conditionId}/reset`)
		.catch((e) => {
			displayErrorMessage(e, 'RuleEngine');
		});
};

export {deleteRule, searchRules, saveUiRule, loadRule, updateRule, getTriggerDetails, resetCounter};
